<template>
  <div class="form-container">
    <form @submit.prevent="handleSubmit">
      <div class="row">
        <div class="form-group col-xl-4 col-lg-6 col-md-6">
          <label for="username">Nom d'utilisateur :</label>
          <input type="text" id="username" v-model.trim="formData.username" required class="form-control"
            pattern="[a-zA-Z0-9]+" title="Le nom d'utilisateur ne doit contenir que des lettres et des chiffres" />
          <small class="text-muted">Lettres et chiffres uniquement</small>
        </div>

        <div class="form-group col-xl-4 col-lg-6 col-md-6">
          <label for="first_name">Prénom :</label>
          <input type="text" id="first_name" v-model.trim="formData.first_name" required class="form-control" />
        </div>

        <div class="form-group col-xl-4 col-lg-4">
          <label for="last_name">Nom :</label>
          <input type="text" id="last_name" v-model.trim="formData.last_name" required class="form-control" />
        </div>
      </div>

      <div class="row">
        <div class="form-group col-xl-4 col-lg-6 col-md-6">
          <label for="email">Email :</label>
          <input type="email" id="email" v-model.trim="formData.email" required class="form-control"
            pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$" />
        </div>

        <div class="form-group col-xl-4 col-lg-6 col-md-6">
          <label for="password">Mot de passe :</label>
          <input type="password" id="password" v-model="formData.password" required class="form-control"
            minlength="8" />
          <small class="text-muted">Au moins 8 caractères, incluant au moins une lettre et un chiffre</small>
        </div>

        <div class="form-group col-xl-4 col-lg-6 col-md-6">
          <label for="role">Rôle :</label>
          <select id="role" v-model="formData.role" required class="form-control">
            <option value="">Sélectionner un rôle</option>
            <option value="employe">Conseiller / Employé</option>
            <option value="admin">Administrateur</option>
            <option value="client">Client</option>
          </select>
        </div>
        <div v-if="formData.role === 'employe'" class="form-group col-xl-4 col-lg-6 col-md-6">
          <label for="role_employe">Rôle du conseiller :</label>
          <select id="role_employe" v-model="formData.role_employe" class="form-control">
            <option value="saisie">Saisie (lecture)</option>
            <option value="validation">Validation et modification</option>
          </select>
        </div>
        <div v-if="formData.role === 'client'" class="form-group col-xl-4 col-lg-6 col-md-6">
          <label for="type_client">Type de client :</label>
          <select id="type_client" v-model="formData.type_client" class="form-control">
            <option value="standard">Client Standard</option>
            <option value="private">Client Privé</option>
          </select>
        </div>
      </div>



      <div v-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>

      <button type="submit" :disabled="loading" class="btn btn-primary">
        {{ loading ? "Création en cours..." : (formData.role === 'client' ? "Créer le client" : "Créer l'utilisateur") }}
      </button>
    </form>
  </div>
</template>

<script>
import axios from "axios";
import { formatDjangoValidationErrors } from "@/utils/apiErrors";
import { createSubmitGuard, formatThrottleMessage } from "@/utils/submitGuard";
import { clearAuthenticatedSession, isAuthenticatedSession } from "@/utils/sessionAuth";

const createUserSubmitGuard = createSubmitGuard(3000);

const TOKEN_KEYS = {
  ACCESS: 'auth_session',
  REFRESH: null /* httpOnly cookie */,
  ROLE: 'user_role'
};

export default {
  name: 'FormUserExtended',
  data() {
    return {
      formData: {
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: '',
        role_employe: 'validation',
        type_client: 'standard',
        est_admin: false,
      },
      loading: false,
      errorMessage: '',
      successMessage: '',
    };
  },
  methods: {

    async handleSubmit() {
      const guarded = await createUserSubmitGuard.run(async () => {
        this.loading = true;
        this.errorMessage = '';
        this.successMessage = '';

        this.formData.est_admin = this.formData.role === 'admin';

        try {
          if (!isAuthenticatedSession()) {
            throw new Error("Token non trouvé");
          }

          const dataToSend = {
            username: this.formData.username.trim(),
            first_name: this.formData.first_name.trim(),
            last_name: this.formData.last_name.trim(),
            email: this.formData.email.trim().toLowerCase(),
            password: this.formData.password,
            role: this.formData.role,
            est_admin: this.formData.est_admin,
            from_back_office: true,
          };
          if (this.formData.role === 'employe') {
            dataToSend.role_employe = this.formData.role_employe;
          }
          if (this.formData.role === 'client') {
            dataToSend.type_client = this.formData.type_client === 'private' ? 'private' : 'standard';
          }

          const response = await axios.post("/api/admin/utilisateurs/create/", dataToSend, {
            withCredentials: true
          });

          if (response.status === 201) {
            this.successMessage = "Utilisateur créé avec succès !";
            try {
              this.$bus.emit('refresh-employes');
            } catch (_) {}
            try {
              if (this.formData.role === 'client') {
                this.$bus.emit('refresh-clients');
                this.$bus.emit('refresh-clients-private');
              }
            } catch (_) {}
            this.resetForm();
            setTimeout(() => {
              this.successMessage = '';
            }, 3000);
          }
        } catch (error) {
          console.error("Erreur lors de la création de l'utilisateur:", error);
          const throttleMsg = formatThrottleMessage(error);
          if (throttleMsg) {
            this.errorMessage = throttleMsg;
          } else if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (typeof errorData === 'object') {
            this.errorMessage = formatDjangoValidationErrors(errorData) || 'Erreur de validation.';
          } else {
            // Jamais le texte brut renvoyé par le serveur.
            this.errorMessage = "Erreur lors de la création de l'utilisateur.";
          }
        } else if (error.response) {
          switch (error.response.status) {
            case 400:
              this.errorMessage = "Données invalides. Veuillez vérifier vos entrées.";
              break;
            case 401:
              console.error("Session expirée, veuillez vous reconnecter.");
              clearAuthenticatedSession();
              /* refresh cookie cleared server-side */;
              localStorage.removeItem(TOKEN_KEYS.ROLE);
              // Swal.fire commenté pour éviter l'affichage sur l'UI
              // Swal.fire(
              //   'Erreur',
              //   'Session expirée ou non autorisé. Veuillez vous reconnecter.',
              //   'error'
              // );
              break;
            case 403:
              this.errorMessage = "Accès non autorisé.";
              break;
            default:
              this.errorMessage = "Erreur lors de la création de l'utilisateur.";
          }
          } else if (error.request) {
            this.errorMessage = "Impossible de contacter le serveur.";
          } else {
            this.errorMessage = "Une erreur est survenue.";
          }
        } finally {
          this.loading = false;
        }
      });
      if (!guarded.ok) {
        this.errorMessage = 'Veuillez patienter avant une nouvelle création.';
      }
    },

    resetForm() {
      this.formData = {
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: '',
        role_employe: 'validation',
        type_client: 'standard',
        est_admin: false,
      };
    }
  }
};
</script>
